import type { DrawerProps } from "@octopusdeploy/design-system-components";
import { ErrorPanel, LinearProgress } from "@octopusdeploy/design-system-components";
import type { ApiOperationStatus } from "@octopusdeploy/octopus-react-client";
import { useAggregateAPIOperationStatus } from "@octopusdeploy/octopus-react-client";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { DialogLayout } from "~/components/DialogLayout/DialogLayout";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { useActiveWorkflowContext } from "~/workflows/ActiveWorkflowContext";
import type { NonPrimaryWorkflowAction, PrimaryWorkflowAction } from "~/workflows/WorkflowAction";
import { renderWorkflowActions } from "~/workflows/getDrawerActionsFromWorkflowActions";
import { useDrawerOutPortalProvider } from "~/workflows/useDrawerNode";
import type { DialogWorkflow, DrawerWorkflow } from "~/workflows/workflows";
export const WorkflowContent = {
    Dialog: DialogWorkflowContent,
    Drawer: DrawerWorkflowContent,
};
interface DialogWorkflowContentProps {
    workflow: DialogWorkflow<unknown, unknown>;
    title: string;
    primaryAction?: PrimaryWorkflowAction;
    actions?: NonPrimaryWorkflowAction[];
    children: ReactNode;
    legacyDoBusyTaskStatus?: ApiOperationStatus;
    onCancel?: () => void;
}
function DialogWorkflowContent({ title, children, actions, primaryAction, legacyDoBusyTaskStatus, onCancel }: DialogWorkflowContentProps) {
    const { isLoading, loadingError, cancelWorkflow, hideCancelButton } = useActiveWorkflowContext();
    const { errors, isInProgress } = useAggregateAPIOperationStatus(legacyDoBusyTaskStatus);
    const handleCancel = () => {
        onCancel?.();
        cancelWorkflow();
    };
    const combinedErrors = useMemo(() => {
        if (loadingError !== null) {
            return [loadingError, ...errors];
        }
        return errors;
    }, [errors, loadingError]);
    const workflowActions = renderWorkflowActions({
        primaryWorkflowAction: primaryAction,
        workflowActions: actions ?? [],
        disableCancelButton: false,
        onCancelClicked: handleCancel,
        hideCancelButton,
    });
    return (<DialogLayout actions={workflowActions} title={title} busy={isLoading || isInProgress} errors={combinedErrors} closeDialog={cancelWorkflow}>
            {children}
        </DialogLayout>);
}
interface DrawerWorkflowContentProps {
    workflow: DrawerWorkflow<unknown, unknown>;
    title: string;
    subtitle?: string;
    primaryAction?: PrimaryWorkflowAction;
    actions?: NonPrimaryWorkflowAction[];
    children: ReactNode;
    legacyDoBusyTaskStatus?: ApiOperationStatus;
    onCancel?: () => void;
}
function DrawerWorkflowContent({ title, children, primaryAction, actions, legacyDoBusyTaskStatus, subtitle, onCancel }: DrawerWorkflowContentProps) {
    const DrawerOutPortal = useDrawerOutPortalProvider();
    const { errors, isInProgress } = useAggregateAPIOperationStatus(legacyDoBusyTaskStatus);
    const { cancelWorkflow, hideCancelButton } = useActiveWorkflowContext();
    const handleCancel = () => {
        onCancel?.();
        cancelWorkflow();
    };
    const renderedActions = renderWorkflowActions({
        disableCancelButton: false,
        onCancelClicked: handleCancel,
        workflowActions: actions ?? [],
        primaryWorkflowAction: primaryAction,
        hideCancelButton,
    });
    // The subtitle can also come from the workflow registration, where it is supplied to the In Portal.
    // If we pass an `undefined` subtitle here, it will override the one from the In Portal, which is not what we want.
    // Instead, we want the In Portal's subtitle (and therefore the registrations subtitle) to be used unless the subtitle
    // passed to this component is defined.
    const extraProps: Partial<DrawerProps> = subtitle
        ? {
            subtitle,
        }
        : {};
    return (<DrawerOutPortal title={title} customActions={renderedActions} {...extraProps}>
            <LinearProgress variant={"indeterminate"} show={isInProgress}/>
            {errors.map(createErrorInfoFromError).map((error, index) => (<ErrorPanel key={index} error={error}/>))}
            {children}
        </DrawerOutPortal>);
}
